import { useTranslation } from 'react-i18next';
import { TextInput, Button, ActionIcon } from '@mantine/core';
import { IconMail, IconTrash, IconPlus } from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import { useState, useEffect } from 'react';
import { localTenant } from '@/services/session-service';
import type { UseMutationResult } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';

interface Props {
    business: Business;
    updateMutation: UseMutationResult<any, Error, Partial<Business>, unknown>;
}

export default function BusinessUsersTab({
    business,
    updateMutation
}: Props): JSX.Element {
    const { t } = useTranslation();
    const tenant = localTenant();

    // Permissions
    const isOwner = tenant && business && business.owner_id === tenant.id;
    const isManager = tenant && business && business.manager_id === tenant.id;
    const canEditBusiness = isOwner || isManager;

    // State for Business email configuration editing
    const [businessDraft, setBusinessDraft] = useState<Business>(business);
    const [businessBackup, setBusinessBackup] = useState<Business>(business);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Initialize state when business data changes
    useEffect(() => {
        setBusinessDraft(business);
        setBusinessBackup(business);
        setErrorMessage(null);
    }, [business]);

    const saveBusinessEmails = async () => {
        setSaving(true);
        setErrorMessage(null);

        try {
            const result = await updateMutation.mutateAsync(businessDraft);
            if (result?.error) {
                const msg = typeof result.error === 'string' ? result.error : (result.error.message || t('error'));
                setErrorMessage(msg);
                notifications.show({
                    title: t('error'),
                    message: msg,
                    color: 'red',
                });
            } else {
                setBusinessBackup({ ...businessDraft });
                notifications.show({
                    title: t('success'),
                    message: t('settings_saved_success'),
                    color: 'green',
                });
            }
        } catch (err: any) {
            const msg = typeof err === 'string' ? err : (err?.message || t('error'));
            setErrorMessage(msg);
            notifications.show({
                title: t('error'),
                message: msg,
                color: 'red',
            });
        } finally {
            setSaving(false);
        }
    };

    const hasBusinessEmailsChanges = () => {
        return JSON.stringify(businessDraft.emails || {}) !== JSON.stringify(businessBackup.emails || {});
    };

    return (
        <div className="space-y-6 max-w-2xl">
            {/* User Accounts & Contact Emails */}
            <div className="solid-box-border">
                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
                    <div className="text-md font-bold text-gray-900">
                        {t('users')} / {t('emails')}
                    </div>
                    {canEditBusiness && (
                        <ActionIcon
                            variant="light" color="black" size="sm" radius="md"
                            onClick={() => {
                                const emailsObj = businessDraft.emails || {};
                                const newKey = `email_${Object.keys(emailsObj).length + 1}`;
                                setBusinessDraft({
                                    ...businessDraft,
                                    emails: { ...emailsObj, [newKey]: '' }
                                });
                            }}
                        >
                            <IconPlus size={14} />
                        </ActionIcon>
                    )}
                </div>

                <div className="flex flex-col gap-3">
                    {Object.entries(businessDraft.emails || {}).length === 0 ? (
                        <div className="text-xs text-gray-500 py-2">
                            {t('no_emails_configured_for_this_business')}
                        </div>
                    ) : (
                        Object.entries(businessDraft.emails || {}).map(([key, value], index) => {
                            const isExisting = !!businessBackup.emails && businessBackup.emails[key] !== undefined && businessBackup.emails[key] !== '';
                            return (
                                <div key={`email-${index}`} className="flex flex-col gap-1 pb-2 border-b border-gray-50/50 last:border-b-0">
                                    <div className="flex gap-2 items-center">
                                        <TextInput
                                            placeholder={t('label')}
                                            className="w-1/3 font-semibold"
                                            value={key || ''}
                                            onChange={(e) => {
                                                const newKey = e.target.value;
                                                const emailsObj = businessDraft.emails || {};
                                                const { [key]: oldVal, ...rest } = emailsObj;
                                                setBusinessDraft({
                                                    ...businessDraft,
                                                    emails: { ...rest, [newKey]: value }
                                                });
                                            }}
                                            disabled={!canEditBusiness || isExisting}
                                        />
                                        <TextInput
                                            placeholder={t('email')}
                                            className="flex-1 font-semibold"
                                            leftSection={<IconMail size={16} />}
                                            value={value || ''}
                                            onChange={(e) => {
                                                const emailsObj = businessDraft.emails || {};
                                                setBusinessDraft({
                                                    ...businessDraft,
                                                    emails: { ...emailsObj, [key]: e.target.value }
                                                });
                                            }}
                                            disabled={!canEditBusiness || isExisting}
                                        />
                                        {canEditBusiness && (
                                            <ActionIcon
                                                color="red" variant="transparent"
                                                onClick={() => {
                                                    const emailsObj = businessDraft.emails || {};
                                                    const { [key]: deleted, ...rest } = emailsObj;
                                                    setBusinessDraft({ ...businessDraft, emails: rest });
                                                }}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {errorMessage && (
                        <div className="text-xs text-red-600 font-medium bg-red-50 p-2.5 rounded-lg border border-red-200">
                            {errorMessage}
                        </div>
                    )}

                    {canEditBusiness && Object.entries(businessDraft.emails || {}).length > 0 && (
                        <div className="flex justify-end mt-4">
                            <Button
                                size="xs"
                                radius="xl"
                                color="black"
                                onClick={saveBusinessEmails}
                                loading={saving}
                                disabled={!hasBusinessEmailsChanges()}
                            >
                                {t('save')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
